import { Company, BuildingPosition, GameState } from '../../types/career';
import BuildingRow from './BuildingRow';
import Street from './Street';
import Character from './Character';
import StreetSign from './StreetSign';

interface GameWorldProps {
  companies: Company[];
  buildingPositions: BuildingPosition[];
  gameState: GameState;
  worldWidth: number;
  viewportWidth: number;
  nearbyBuildingId: string | null;
  onBuildingClick: (id: string, x: number) => void;
  onWorldClick: (x: number) => void;
}

const GameWorld = ({
  companies,
  buildingPositions,
  gameState,
  worldWidth,
  viewportWidth,
  nearbyBuildingId,
  onBuildingClick,
  onWorldClick,
}: GameWorldProps) => {
  // Check if there's more content in each direction
  const canScrollLeft = gameState.viewportOffset > 10;
  const canScrollRight = gameState.viewportOffset < worldWidth - viewportWidth - 10;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left + gameState.viewportOffset;

    // Check if clicked on a building
    const clickedBuilding = buildingPositions.find(pos => {
      return clickX >= pos.x && clickX <= pos.x + pos.width;
    });

    if (clickedBuilding) {
      onBuildingClick(clickedBuilding.id, clickedBuilding.x + clickedBuilding.width / 2);
    } else {
      onWorldClick(clickX);
    }
  };

  return (
    <div
      className="relative overflow-hidden cursor-pointer"
      style={{
        height: '520px',
        backgroundColor: '#87CEEB', // Sky blue background
      }}
      onClick={handleClick}
    >
      {/* Sky gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, #87CEEB 0%, #B0E0E6 60%, #E0F0FF 100%)',
        }}
      />

      {/* Clouds (decorative) */}
      <div className="absolute" style={{ top: '20px', left: '10%' }}>
        <div className="bg-white rounded-full opacity-80" style={{ width: '60px', height: '30px' }} />
        <div className="bg-white rounded-full opacity-80 absolute" style={{ width: '40px', height: '25px', top: '-10px', left: '30px' }} />
      </div>
      <div className="absolute" style={{ top: '40px', left: '60%' }}>
        <div className="bg-white rounded-full opacity-80" style={{ width: '80px', height: '35px' }} />
        <div className="bg-white rounded-full opacity-80 absolute" style={{ width: '50px', height: '30px', top: '-15px', left: '40px' }} />
      </div>

      {/* Scrollable game world container */}
      <div
        className="absolute"
        style={{
          width: `${worldWidth}px`,
          height: '100%',
          transform: `translateX(-${gameState.viewportOffset}px)`,
          transition: 'transform 0.05s linear',
        }}
      >
        {/* Top row of buildings */}
        <div className="absolute top-12 left-0 right-0">
          <BuildingRow
            companies={companies}
            positions={buildingPositions}
            side="top"
            nearbyBuildingId={nearbyBuildingId}
            onBuildingClick={(id) => {
              const pos = buildingPositions.find(p => p.id === id);
              if (pos) onBuildingClick(id, pos.x + pos.width / 2);
            }}
          />
        </div>

        {/* Street */}
        <div className="absolute left-0 right-0" style={{ top: '220px' }}>
          <Street width={worldWidth} />

          {/* Street signs between buildings (except first and last) */}
          {buildingPositions.length > 2 && buildingPositions.slice(1, -1).map((pos, index) => {
            const signX = pos.x - 40; // Position sign in the gap before this building
            const leftCount = index + 1; // Buildings to the left
            const rightCount = buildingPositions.length - index - 2; // Buildings to the right
            return (
              <StreetSign
                key={`sign-${pos.id}`}
                x={signX}
                leftCount={leftCount}
                rightCount={rightCount}
              />
            );
          })}
        </div>

        {/* Character on the street */}
        <div className="absolute left-0 right-0" style={{ top: '220px', height: '80px' }}>
          <Character
            x={gameState.characterX}
            isWalking={gameState.isWalking}
            facingDirection={gameState.facingDirection}
          />
        </div>

        {/* Bottom row of buildings */}
        <div className="absolute left-0 right-0" style={{ top: '300px' }}>
          <BuildingRow
            companies={companies}
            positions={buildingPositions}
            side="bottom"
            nearbyBuildingId={nearbyBuildingId}
            onBuildingClick={(id) => {
              const pos = buildingPositions.find(p => p.id === id);
              if (pos) onBuildingClick(id, pos.x + pos.width / 2);
            }}
          />
        </div>

        {/* Ground/sidewalk below bottom buildings */}
        <div
          className="absolute left-0 right-0 bg-gray-300"
          style={{
            top: '500px',
            height: '20px',
          }}
        />
      </div>

      {/* Timeline indicator */}
      <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded text-sm">
        &larr; Most Recent | Oldest &rarr;
      </div>

      {/* Left scroll indicator */}
      {canScrollLeft && (
        <div className="absolute left-0 top-0 bottom-0 w-16 flex items-center justify-start pl-2 pointer-events-none"
          style={{
            background: 'linear-gradient(to right, rgba(0,0,0,0.3) 0%, transparent 100%)',
          }}
        >
          <div className="text-white text-3xl animate-pulse opacity-80">
            ‹
          </div>
        </div>
      )}

      {/* Right scroll indicator */}
      {canScrollRight && (
        <div className="absolute right-0 top-0 bottom-0 w-16 flex items-center justify-end pr-2 pointer-events-none"
          style={{
            background: 'linear-gradient(to left, rgba(0,0,0,0.3) 0%, transparent 100%)',
          }}
        >
          <div className="text-white text-3xl animate-pulse opacity-80">
            ›
          </div>
        </div>
      )}
    </div>
  );
};

export default GameWorld;
